import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Box, Avatar, Typography } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark as themCode } from "react-syntax-highlighter/dist/esm/styles/prism";
// import remarkGridTable from "@adobe/remark-gridtables";
import rehypeKatex from "rehype-katex";
import rehypeStringify from "rehype-stringify";
import remarkMath from "remark-math";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import remarkGfm from "remark-gfm";
import { mdiClockAlertOutline, mdiAlertCircle, mdiAlertOutline } from "@mdi/js";
import { AttachmentList } from "./AttachmentPreview";
import Icon from "@mdi/react";

// ── Sanitização de Markdown incompleto durante streaming ────────

/**
 * Fecha tags Markdown que ficaram abertas durante o streaming,
 * evitando que o layout "quebre" enquanto os tokens ainda chegam.
 */
function sanitizeStreamingMarkdown(md: string): string {
	let result = md;

	// Fechar blocos de código abertos (``` sem par)
	const fenceMatches = result.match(/```/g);
	if (fenceMatches && fenceMatches.length % 2 !== 0) {
		result += "\n```";
	}

	// Fechar código inline aberto (` sem par) — só se não estiver dentro de fence
	if (!fenceMatches || fenceMatches.length % 2 === 0) {
		const backtickMatches = result.match(/(?<!`)`(?!`)/g);
		if (backtickMatches && backtickMatches.length % 2 !== 0) {
			result += "`";
		}
	}

	// Fechar negrito/itálico abertos
	const boldMatches = result.match(/\*\*/g);
	if (boldMatches && boldMatches.length % 2 !== 0) {
		result += "**";
	}

	const italicMatches = result.match(/(?<!\*)\*(?!\*)/g);
	if (italicMatches && italicMatches.length % 2 !== 0) {
		result += "*";
	}

	return result;
}

/**
 * EK-409: card de alerta inline no chat quando o backend detecta falha
 * critica ou alucinacao na resposta do assistente. Substitui visualmente
 * o markdown da bolha por um card vermelho/laranja chamativo.
 */
export interface ErrorAlertSpec {
	level: "error" | "warning";
	title: string;
	message: string;
	details?: Record<string, any>;
}

// Tipagem das Props de Mensagem
export interface MessageProps {
	id: string;
	children?: string | null; // Markdown content
	incoming?: boolean;
	outgoing?: boolean;
	avatarUrl?: string | (string | undefined)[];
	senderName?: string;
	thinking?: string;
	pending?: boolean;
	error?: string;
	/** Quando true, ativa cursor pulsante e sanitização de markdown incompleto. */
	isStreaming?: boolean;
	/** EK-408 polish: anexos persistidos exibidos abaixo do texto da bolha. */
	attachments?: import("shared/types/MessageContracts").PublicMessageAttachmentDTO[];
	/** EK-409: alerta de falha critica detectada pelo backend (substitui markdown). */
	errorAlert?: ErrorAlertSpec;
	/**
	 * Path de icone MDI usado como fallback quando nao ha `avatarUrl`. Util para
	 * o avatar do assistente IA (mdiRobotHappy), do lider (mdiShieldCrown), etc.
	 * Quando ausente, cai no fallback antigo (inicial do nome).
	 */
	avatarFallbackIconPath?: string;
}

const MessageInner: React.FC<MessageProps> = ({ id, children, incoming, outgoing, avatarUrl, senderName, thinking, pending, error, isStreaming, attachments, errorAlert, avatarFallbackIconPath }) => {
	const isIncoming = incoming || !outgoing;
	const isThinking = thinking && (!children || children.trim() === "");
	const avatarUrls = Array.isArray(avatarUrl) ? avatarUrl : [avatarUrl];

	// Sanitiza markdown durante streaming para evitar layout quebrado
	const markdownContent = useMemo(() => {
		if (!children) return children;
		return isStreaming ? sanitizeStreamingMarkdown(children) : children;
	}, [children, isStreaming]);

	return (
		<Box className={clsx(styles.messageRow, isIncoming && styles.incoming, outgoing && styles.outgoing)}>
			{/* Avatar component from MUI handles image or fallback */}
			<div className={styles.avatars}>
				{avatarUrls.map((url, index) => (
					<Avatar
						key={index}
						className={clsx(styles.avatar, !url && avatarFallbackIconPath && styles.avatarIconFallback)}
						src={url}
						alt={senderName || "User"}
					>
						{!url && avatarFallbackIconPath ? (
							<Icon path={avatarFallbackIconPath} size={1.1} />
						) : (
							!url && (senderName ? senderName[0]?.toUpperCase() : "?")
						)}
					</Avatar>
				))}
			</div>

			<Box className={styles.messageContent}>
				{senderName && (
					<Typography
						variant="caption"
						className={styles.senderName}
					>
						{senderName}
					</Typography>
				)}
				{isThinking && <Box className={styles.thinking}>{thinking}</Box>}
				{errorAlert ? (
					<Box className={clsx(styles.errorAlert, errorAlert.level === "warning" && styles.errorAlertWarning)}>
						<div className={styles.errorAlertHeader}>
							<Icon path={errorAlert.level === "warning" ? mdiAlertOutline : mdiAlertCircle} size={1} />
							<span className={styles.errorAlertTitle}>{errorAlert.title}</span>
						</div>
						<p className={styles.errorAlertMessage}>{errorAlert.message}</p>
						{errorAlert.details && Object.keys(errorAlert.details).length > 0 && (
							<details className={styles.errorAlertDetails}>
								<summary>Detalhes técnicos</summary>
								<pre>{JSON.stringify(errorAlert.details, null, 2)}</pre>
							</details>
						)}
					</Box>
				) : null}
				<Box
					className={styles.bubble}
					style={{
						opacity: pending || error ? 0.3 : 1,
						display: errorAlert || !children || children.trim() === "" ? "none" : undefined,
					}}
				>
					<ReactMarkdown
						remarkPlugins={[remarkParse, remarkMath, remarkRehype, rehypeKatex, rehypeStringify, remarkGfm]}
						components={{
							code(props) {
								const { children, className, node, ...rest } = props;
								const match = /language-(\w+)/.exec(className || "");
								return match ? (
									<SyntaxHighlighter
										{...(rest as any)}
										PreTag={({ children, style, className, ...props }) => {
											return (
												<div
													className={clsx(styles.code, className)}
													{...props}
												>
													{children}
												</div>
											);
										}}
										children={String(children).replace(/\n$/, "")}
										language={match[1]}
										style={themCode}
									/>
								) : (
									<div className={clsx(styles.code)}>
										<code
											{...(rest as any)}
											className={className}
										>
											{children}
										</code>
									</div>
								);
							},
						}}
					>
						{markdownContent}
					</ReactMarkdown>
					{/* {isStreaming && <span className={styles.streamingCursor} />} */}
				</Box>
				{attachments && attachments.length > 0 && <AttachmentList attachments={attachments} />}
				{pending && <Box className={styles.pending}>Enviando...</Box>}
				{error && (
					<Box className={styles.error}>
						{(error || "Erro ao enviar") + " "}
						<Icon
							path={mdiClockAlertOutline}
							size={0.7}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export const Message = React.memo(MessageInner);
